import React, { useEffect, useState, createContext, useContext } from 'react';
interface CurrencyData {
  code: string;
  symbol: string;
  rate: number; // Rate relative to USD
  name: string;
}
interface CurrencyContextType {
  currency: CurrencyData;
  formatPrice: (usdPrice: number) => string;
  isLoading: boolean;
  country: string;
}
const currencies: Record<string, CurrencyData> = {
  MX: {
    code: 'MXN',
    symbol: '$',
    rate: 17.5,
    name: 'Peso Mexicano'
  },
  AR: {
    code: 'ARS',
    symbol: '$',
    rate: 875,
    name: 'Peso Argentino'
  },
  CO: {
    code: 'COP',
    symbol: '$',
    rate: 4000,
    name: 'Peso Colombiano'
  },
  CL: {
    code: 'CLP',
    symbol: '$',
    rate: 950,
    name: 'Peso Chileno'
  },
  PE: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.75,
    name: 'Sol Peruano'
  },
  ES: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92,
    name: 'Euro'
  },
  BR: {
    code: 'BRL',
    symbol: 'R$',
    rate: 5.0,
    name: 'Real Brasileño'
  },
  US: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    name: 'Dólar'
  },
  DEFAULT: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    name: 'Dólar'
  }
};
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currency, setCurrency] = useState<CurrencyData>(currencies.DEFAULT);
  const [country, setCountry] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const detectCountry = async () => {
      // Check if we have a cached country in localStorage
      const cachedCountry = localStorage.getItem('user_country');
      if (cachedCountry && currencies[cachedCountry]) {
        setCurrency(currencies[cachedCountry]);
        setCountry(cachedCountry);
        setIsLoading(false);
        return;
      }
      try {
        // Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_code) {
          const countryCode = data.country_code;
          setCountry(countryCode);
          // Cache the country for future visits
          localStorage.setItem('user_country', countryCode);
          // Set currency based on country
          if (currencies[countryCode]) {
            setCurrency(currencies[countryCode]);
          } else {
            // Default to USD for unsupported countries
            setCurrency(currencies.DEFAULT);
          }
        }
      } catch (error) {
        console.error('Error detecting country:', error);
        // Fallback to USD
        setCurrency(currencies.DEFAULT);
      } finally {
        setIsLoading(false);
      }
    };
    detectCountry();
  }, []);
  const formatPrice = (usdPrice: number): string => {
    const localPrice = usdPrice * currency.rate;
    // Format based on currency
    if (currency.code === 'USD' || currency.code === 'EUR') {
      return `${currency.symbol}${localPrice.toFixed(2)}`;
    }
    // For currencies with large numbers (ARS, COP, CLP), show without decimals
    if (currency.rate > 100) {
      return `${currency.symbol}${Math.round(localPrice).toLocaleString()}`;
    }
    return `${currency.symbol}${localPrice.toFixed(2)}`;
  };
  return <CurrencyContext.Provider value={{
    currency,
    formatPrice,
    isLoading,
    country
  }} data-id="element-628">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}